import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FolderKanban, Ticket, FileText, Bell, ArrowRight, Plus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const STAT = [
  { key: "active_projects", label: "Active projects", icon: FolderKanban, to: "/dashboard/projects" },
  { key: "quotes", label: "Quotes", icon: FileText, to: "/dashboard/quotes" },
  { key: "open_tickets", label: "Open tickets", icon: Ticket, to: "/dashboard/tickets" },
  { key: "unread_notifications", label: "Notifications", icon: Bell, to: "/dashboard/notifications" },
];

export default function PortalDashboard() {
  const { data } = useQuery({ queryKey: ["portal-dashboard"], queryFn: async () => (await api.get("/portal/dashboard")).data });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Your projects, quotes and support in one place.</p>
        </div>
        <Button asChild className="rounded-full" data-testid="portal-new-ticket"><Link to="/dashboard/tickets"><Plus className="mr-2 h-4 w-4" /> New request</Link></Button>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {STAT.map((s) => (
          <Link key={s.key} to={s.to} className="bs-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-soft" data-testid={`portal-stat-${s.key}`}>
            <div className="flex items-center justify-between">
              <div className="rounded-xl bg-primary/12 p-2.5 text-primary"><s.icon className="h-5 w-5" /></div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="mt-4 font-display text-3xl font-black">{data?.[s.key] ?? 0}</p>
            <p className="text-sm text-muted-foreground">{s.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="font-display text-lg font-bold">Recent projects</h2>
        <div className="mt-4 space-y-3">
          {(data?.recent_projects || []).length === 0 && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No projects yet. Once you start a project with us, it will appear here.</p>}
          {(data?.recent_projects || []).map((p) => (
            <Link key={p.id} to={`/dashboard/projects/${p.id}`} className="block bs-card p-5 hover:shadow-soft">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold">{p.title}</h3>
                <Badge variant="secondary">{p.status}</Badge>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Progress value={p.progress || 0} className="h-2" />
                <span className="text-xs text-muted-foreground">{p.progress || 0}%</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
